import apiClient from './api';

export interface SlotCreationData {
  date: string;
  start_time: string;
  end_time: string;
  capacity: number;
  price_coins: number;
  price_cash: number;
}

export interface BulkSlotCreationData {
  start_date: string;
  end_date: string;
  days_of_week: number[];
  time_slots: Array<{
    start_time: string;
    end_time: string;
    capacity: number;
    price_coins: number;
    price_cash: number;
  }>;
}

export interface SlotUpdateData {
  capacity?: number;
  price_coins?: number;
  price_cash?: number;
  is_active?: boolean;
}

export interface Slot {
  id: number;
  gym_id: number;
  date: string;
  start_time: string;
  end_time: string;
  capacity: number;
  booked_count: number;
  available_capacity: number;
  price_coins: number;
  price_cash: number;
  is_active: boolean;
  occupancy_percentage?: number;
  created_at?: string;
}

export interface SlotAvailability {
  id: number;
  start_time: string;
  end_time: string;
  capacity: number;
  booked_count: number;
  available_seats: number;
  is_full: boolean;
}

class SlotService {
  /**
   * Create single slot
   */
  async createSlot(gymId: number, data: SlotCreationData): Promise<{ slotId: number; message: string; slot: Slot }> {
    return apiClient.post<{ slotId: number; message: string; slot: Slot }>(
      `/partners/gyms/${gymId}/slots`,
      data
    );
  }

  /**
   * Create bulk slots (recurring)
   */
  async createBulkSlots(
    gymId: number,
    data: BulkSlotCreationData
  ): Promise<{ message: string; slots_created: number; date_range: string; time_slots_per_day: number }> {
    return apiClient.post<{ message: string; slots_created: number; date_range: string; time_slots_per_day: number }>(
      `/partners/gyms/${gymId}/slots/bulk`,
      data
    );
  }

  /**
   * Get all slots for gym
   */
  async getGymSlots(
    gymId: number,
    filters?: {
      date?: string;
      date_from?: string;
      date_to?: string;
      is_active?: boolean;
    }
  ): Promise<{ slots: Slot[]; total_count: number }> {
    const params = new URLSearchParams();
    if (filters?.date) params.append('date', filters.date);
    if (filters?.date_from) params.append('date_from', filters.date_from);
    if (filters?.date_to) params.append('date_to', filters.date_to);
    if (filters?.is_active !== undefined) params.append('is_active', filters.is_active.toString());

    return apiClient.get<{ slots: Slot[]; total_count: number }>(
      `/partners/gyms/${gymId}/slots${params.toString() ? '?' + params.toString() : ''}`
    );
  }

  /**
   * Update slot
   */
  async updateSlot(gymId: number, slotId: number, data: SlotUpdateData): Promise<{ message: string; slot: Slot }> {
    return apiClient.put<{ message: string; slot: Slot }>(
      `/partners/gyms/${gymId}/slots/${slotId}`,
      data
    );
  }

  /**
   * Delete slot
   */
  async deleteSlot(gymId: number, slotId: number): Promise<{ message: string }> {
    return apiClient.delete<{ message: string }>(`/partners/gyms/${gymId}/slots/${slotId}`);
  }

  /**
   * Get slot availability for specific date
   */
  async getSlotAvailability(gymId: number, date: string): Promise<{ gym_id: number; date: string; slots: SlotAvailability[] }> {
    return apiClient.get<{ gym_id: number; date: string; slots: SlotAvailability[] }>(
      `/partners/gyms/${gymId}/slots/availability?date=${date}`
    );
  }
}

const slotService = new SlotService();
export default slotService;
